import { useCallback, useMemo, useRef, useState } from 'react';

import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import {
  CALENDAR_GRID_STEP_MINUTES_VALUES,
  CALENDAR_INTERVAL_HEIGHT_BY_SIZE,
  DEFAULT_APPOINTMENT_PREVIEW_FIELDS,
  DEFAULT_CALENDAR_INTERVAL_HEIGHT_SIZE,
  DEFAULT_GRID_STEP_MINUTES,
  DEFAULT_MAX_VISIBLE_COLUMN_COUNT,
  MAX_SELECTABLE_VISIBLE_COLUMN_COUNT,
  MIN_SELECTABLE_VISIBLE_COLUMN_COUNT,
} from '@pages/Calendar/data';
import type {
  AppointmentPreviewField,
  CalendarDisplaySettings,
  CalendarGridStepMinutes,
  CalendarIntervalHeightSize,
} from '@pages/Calendar/data';
import {
  areAppointmentPreviewFieldsEqual,
  normalizeAppointmentPreviewFields,
} from '@pages/Calendar/utils/normalizeAppointmentPreviewFields';

import { useCalendarSettingsEffects } from './useCalendarSettingsEffects';

const CALENDAR_DISPLAY_SETTINGS_STORAGE_KEY = 'calendarDisplaySettings';
const CALENDAR_SETTINGS_STORAGE_LEGACY_SUFFIX = 'CalendarSettings';
const LEGACY_SETTINGS_STORAGE_PREFIX = `New${CALENDAR_SETTINGS_STORAGE_LEGACY_SUFFIX}`;

const DEFAULT_CALENDAR_DISPLAY_SETTINGS: CalendarDisplaySettings = {
  intervalHeightSize: DEFAULT_CALENDAR_INTERVAL_HEIGHT_SIZE,
  preferredMaxColumns: DEFAULT_MAX_VISIBLE_COLUMN_COUNT,
  gridStepMinutes: DEFAULT_GRID_STEP_MINUTES,
  previewFields: [...DEFAULT_APPOINTMENT_PREVIEW_FIELDS],
};

const isCalendarIntervalHeightSize = (
  value: unknown
): value is CalendarIntervalHeightSize =>
  typeof value === 'string' && value in CALENDAR_INTERVAL_HEIGHT_BY_SIZE;

const normalizePreferredMaxColumns = (value: number): number => {
  if (!Number.isFinite(value)) {
    return DEFAULT_MAX_VISIBLE_COLUMN_COUNT;
  }

  return Math.min(
    Math.max(Math.round(value), MIN_SELECTABLE_VISIBLE_COLUMN_COUNT),
    MAX_SELECTABLE_VISIBLE_COLUMN_COUNT
  );
};

const isCalendarGridStepMinutes = (
  value: unknown
): value is CalendarGridStepMinutes =>
  typeof value === 'number' &&
  CALENDAR_GRID_STEP_MINUTES_VALUES.includes(value as CalendarGridStepMinutes);

const normalizeCalendarDisplaySettings = (
  settings: CalendarDisplaySettings
): CalendarDisplaySettings => ({
  intervalHeightSize: isCalendarIntervalHeightSize(settings.intervalHeightSize)
    ? settings.intervalHeightSize
    : DEFAULT_CALENDAR_INTERVAL_HEIGHT_SIZE,
  preferredMaxColumns: normalizePreferredMaxColumns(
    settings.preferredMaxColumns
  ),
  gridStepMinutes: isCalendarGridStepMinutes(settings.gridStepMinutes)
    ? settings.gridStepMinutes
    : DEFAULT_GRID_STEP_MINUTES,
  previewFields: normalizeAppointmentPreviewFields(settings.previewFields),
});

const areCalendarDisplaySettingsEqual = (
  leftSettings: CalendarDisplaySettings,
  rightSettings: CalendarDisplaySettings
) =>
  leftSettings.intervalHeightSize === rightSettings.intervalHeightSize &&
  leftSettings.preferredMaxColumns === rightSettings.preferredMaxColumns &&
  leftSettings.gridStepMinutes === rightSettings.gridStepMinutes &&
  areAppointmentPreviewFieldsEqual(
    leftSettings.previewFields,
    rightSettings.previewFields
  );

const getSettingsStorageKey = ({
  userId,
  database,
  activeBranchId,
  storagePrefix,
}: {
  userId: number | null;
  database: string | null;
  activeBranchId: number | null;
  storagePrefix: string;
}): string | null => {
  if (!userId || !database || !activeBranchId) {
    return null;
  }

  return `${storagePrefix}:${database}:${activeBranchId}:${userId}`;
};

export const useCalendarSettings = () => {
  const { userId, database, activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const [calendarDisplaySettings, setCalendarDisplaySettingsState] =
    useState<CalendarDisplaySettings>(DEFAULT_CALENDAR_DISPLAY_SETTINGS);
  const calendarDisplaySettingsRef = useRef<CalendarDisplaySettings>(
    DEFAULT_CALENDAR_DISPLAY_SETTINGS
  );

  const storageKey = useMemo(
    () =>
      getSettingsStorageKey({
        userId,
        database,
        activeBranchId,
        storagePrefix: CALENDAR_DISPLAY_SETTINGS_STORAGE_KEY,
      }),
    [activeBranchId, database, userId]
  );

  const legacyStorageKey = useMemo(
    () =>
      getSettingsStorageKey({
        userId,
        database,
        activeBranchId,
        storagePrefix: LEGACY_SETTINGS_STORAGE_PREFIX,
      }),
    [activeBranchId, database, userId]
  );

  const intervalHeightPx = useMemo(
    () =>
      CALENDAR_INTERVAL_HEIGHT_BY_SIZE[
        calendarDisplaySettings.intervalHeightSize
      ],
    [calendarDisplaySettings.intervalHeightSize]
  );

  const saveCalendarDisplaySettings = useCallback(
    (settings: CalendarDisplaySettings) => {
      if (!storageKey) {
        return;
      }

      try {
        const serializedSettings = JSON.stringify(settings);

        if (window.localStorage.getItem(storageKey) === serializedSettings) {
          return;
        }

        window.localStorage.setItem(storageKey, serializedSettings);
      } catch (error) {
        showNotification(error);
      }
    },
    [showNotification, storageKey]
  );

  const commitCalendarDisplaySettings = useCallback(
    ({
      settings,
      shouldSave,
    }: {
      settings: CalendarDisplaySettings;
      shouldSave: boolean;
    }) => {
      const nextSettings = normalizeCalendarDisplaySettings(settings);

      if (
        areCalendarDisplaySettingsEqual(
          calendarDisplaySettingsRef.current,
          nextSettings
        )
      ) {
        return;
      }

      calendarDisplaySettingsRef.current = nextSettings;
      setCalendarDisplaySettingsState(nextSettings);

      if (shouldSave) {
        saveCalendarDisplaySettings(nextSettings);
      }
    },
    [saveCalendarDisplaySettings]
  );

  const setCalendarDisplaySettings = useCallback(
    (value: CalendarDisplaySettings) => {
      commitCalendarDisplaySettings({
        settings: value,
        shouldSave: false,
      });
    },
    [commitCalendarDisplaySettings]
  );

  const updateCalendarDisplaySettings = useCallback(
    (value: CalendarDisplaySettings) => {
      commitCalendarDisplaySettings({
        settings: value,
        shouldSave: true,
      });
    },
    [commitCalendarDisplaySettings]
  );

  const updatePreferredMaxColumns = useCallback(
    (value: number) => {
      commitCalendarDisplaySettings({
        settings: {
          ...calendarDisplaySettingsRef.current,
          preferredMaxColumns: value,
        },
        shouldSave: true,
      });
    },
    [commitCalendarDisplaySettings]
  );

  const updateIntervalHeightSize = useCallback(
    (value: CalendarIntervalHeightSize) => {
      commitCalendarDisplaySettings({
        settings: {
          ...calendarDisplaySettingsRef.current,
          intervalHeightSize: value,
        },
        shouldSave: true,
      });
    },
    [commitCalendarDisplaySettings]
  );

  const updateGridStepMinutes = useCallback(
    (value: CalendarGridStepMinutes) => {
      commitCalendarDisplaySettings({
        settings: {
          ...calendarDisplaySettingsRef.current,
          gridStepMinutes: value,
        },
        shouldSave: true,
      });
    },
    [commitCalendarDisplaySettings]
  );

  const updatePreviewFields = useCallback(
    (value: AppointmentPreviewField[]) => {
      commitCalendarDisplaySettings({
        settings: {
          ...calendarDisplaySettingsRef.current,
          previewFields: value,
        },
        shouldSave: true,
      });
    },
    [commitCalendarDisplaySettings]
  );

  const updateMaxVisibleColumnCount = useCallback(
    (value: number) => {
      updatePreferredMaxColumns(value);
    },
    [updatePreferredMaxColumns]
  );

  const maxVisibleColumnCount = useMemo(
    () => calendarDisplaySettings.preferredMaxColumns,
    [calendarDisplaySettings.preferredMaxColumns]
  );

  useCalendarSettingsEffects({
    storageKey,
    legacyStorageKey,
    setCalendarDisplaySettings,
    showNotification,
  });

  return {
    calendarDisplaySettings,
    intervalHeightPx,
    intervalHeightSize: calendarDisplaySettings.intervalHeightSize,
    gridStepMinutes: calendarDisplaySettings.gridStepMinutes,
    maxVisibleColumnCount,
    preferredMaxColumns: calendarDisplaySettings.preferredMaxColumns,
    previewFields: calendarDisplaySettings.previewFields,
    updateCalendarDisplaySettings,
    updateGridStepMinutes,
    updateIntervalHeightSize,
    updateMaxVisibleColumnCount,
    updatePreferredMaxColumns,
    updatePreviewFields,
  };
};
